import { useState } from "react";
import Button from "../ui/Button.jsx";
import { useToast } from "../../hooks/useToast.js";
import { mapAdminError } from "../../utils/adminApiErrors.js";

const inlineAlertStyle = { margin: "0 0 12px", color: "var(--critical)", fontSize: "13px" };

export default function DeletePanel({ user, onCancel, onDone }) {
  const { show } = useToast();
  const [confirmText, setConfirmText] = useState("");
  const [loading, setLoading] = useState(false);

  const canDelete = confirmText === "DELETE";
  const confirmError =
    confirmText.length > 0 && !canDelete ? "Type DELETE exactly to confirm." : null;

  async function handleDelete() {
    if (!canDelete) return;
    setLoading(true);
    try {
      const res = await fetch(`/api/admin/users/${encodeURIComponent(user.id)}`, {
        method: "DELETE",
        credentials: "include",
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        if (body?.error === "must_deactivate_first") {
          show("User must be deactivated before deletion", "error");
        } else {
          show(mapAdminError(body?.error), "error");
        }
        return;
      }
      show("User deleted (tombstoned).", "success");
      onDone?.();
    } catch {
      show("Failed to delete user. Try again.", "error");
    } finally {
      setLoading(false);
    }
  }

  return (
    <div>
      <h3 style={{ margin: "0 0 10px", fontSize: "18px", fontWeight: 600 }}>
        Delete {user?.name ? `${user.name.first || ""} ${user.name.last || ""}`.trim() || user.email : user.email}?
      </h3>
      <p className="text-secondary" style={{ margin: "0 0 8px", fontSize: "14px", lineHeight: 1.5 }}>
        This user is already deactivated. Deleting will permanently remove access. Their email will be released for future re-invitation.
      </p>
      <p style={{ margin: "0 0 12px", fontSize: "13px" }}>
        <strong>Email to tombstone:</strong> {user?.email || "—"}
      </p>

      <label style={{ display: "block", fontSize: "12px", fontWeight: 600, marginBottom: "6px" }}>
        Type DELETE to confirm
      </label>
      <input
        value={confirmText}
        onChange={(e) => setConfirmText(e.target.value)}
        style={{
          width: "100%",
          padding: "10px 12px",
          fontSize: "14px",
          borderRadius: "4px",
          border: "1px solid var(--border-bright)",
          background: "var(--bg-elevated)",
          color: "var(--text-primary)",
          fontFamily: "inherit",
          boxSizing: "border-box",
          marginBottom: "16px",
        }}
      />
      {confirmError ? (
        <p role="alert" style={inlineAlertStyle}>
          {confirmError}
        </p>
      ) : null}

      <div style={{ display: "flex", gap: "10px" }}>
        <Button variant="secondary" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" onClick={handleDelete} loading={loading} disabled={!canDelete || loading}>
          Delete
        </Button>
      </div>
    </div>
  );
}
